import {
	AFFILIATED_ORGANIZATION_SORT_KEYS,
	AFFILIATED_ORGANIZATION_TYPES,
	type AffiliatedOrganizationListItem,
	type AffiliatedOrganizationSortKey,
	type AffiliatedOrganizationType,
} from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { type AffiliatedOrganizationFormTarget, AffiliatedOrganizationFormModal } from "../../components/affiliated-organizations/affiliated-organization-form-modal";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import {
	affiliatedOrganizationStatsOptions,
	affiliatedOrganizationsListOptions,
	deleteAffiliatedOrganization,
	invalidateAffiliatedOrganizations,
} from "../../lib/affiliated-organizations/queries";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";

export const Route = createFileRoute("/_app/settings/affiliated-organizations")({
	validateSearch: listSearch(AFFILIATED_ORGANIZATION_SORT_KEYS, { type: z.enum(AFFILIATED_ORGANIZATION_TYPES) }),
	component: AffiliatedOrganizationsPage,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface AffiliatedOrganizationFilters extends Record<string, unknown> {
	search: string;
	type: "" | AffiliatedOrganizationType;
}
const EMPTY_FILTERS: AffiliatedOrganizationFilters = { search: "", type: "" };

function AffiliatedOrganizationsPage() {
	const { t } = useTranslation("affiliatedOrganizations");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<AffiliatedOrganizationFormTarget | null>(null);

	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) => affiliatedOrganizationsListOptions({ ...q, type: search.type }),
		columnsKey: "affiliated-organizations",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): AffiliatedOrganizationFilters => ({ search: s.search ?? "", type: s.type ?? "" }),
			toUrl: (f) => ({ search: f.search.trim() || undefined, type: f.type || undefined }),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	// Unfiltered counts per type for the strip and the type chips — one aggregate statement.
	const stats = useQuery(affiliatedOrganizationStatsOptions());
	const countByType = stats.data ? new Map(stats.data.by_type.map((b) => [b.type, b.count])) : undefined;
	const del = useConfirmDelete<AffiliatedOrganizationListItem>({
		remove: (r) => deleteAffiliatedOrganization(r.affiliated_organization_id),
		invalidate: invalidateAffiliatedOrganizations,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	const total = page.list.data?.total;
	const typeOptions = AFFILIATED_ORGANIZATION_TYPES.map((value) => ({ value, label: t(`type.${value}`), count: countByType ? (countByType.get(value) ?? 0) : undefined }));
	const sort = sortSelect<AffiliatedOrganizationSortKey>(
		search,
		"name:asc",
		[
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "type:asc", label: tc("sort.typeAsc") },
			{ value: "type:desc", label: tc("sort.typeDesc") },
			{ value: "created_at:desc", label: tc("sort.newest") },
			{ value: "created_at:asc", label: tc("sort.oldest") },
		],
		page.patch,
	);

	const columns: ColumnDef<AffiliatedOrganizationListItem, AffiliatedOrganizationSortKey>[] = [
		{
			key: "name",
			header: t("table.name"),
			sortable: true,
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => <PrimaryCell>{r.name}</PrimaryCell>,
		},
		{
			key: "type",
			header: t("table.type"),
			sortable: true,
			minWidth: "9rem",
			cell: (r) =>
				r.type ? (
					<ListTag>{t(`type.${r.type}`, { defaultValue: r.type })}</ListTag>
				) : (
					<span className="text-base-content/30" title={t("table.noTypeTitle")}>
						—
					</span>
				),
		},
		usageColumn("cards_count", t("table.cards"), t("table.cardsTitle")),
		timestampColumn("created_at", t("table.created"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addOrganization")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("stats.total"), value: total ?? "—", note: page.hasFilters ? t("stats.matching") : undefined },
					// A handful of fixed types: every one gets its cell, zero included.
					...(stats.data ? stats.data.by_type.map((b) => ({ key: b.type ?? "none", label: b.type ? t(`type.${b.type}`) : t("stats.noType"), value: b.count })) : []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={sort}
				chips={[
					{
						kind: "options",
						key: "type",
						label: t("filters.typeChips"),
						options: typeOptions,
						value: search.type ? [search.type] : [],
						onChange: (v) => page.patch({ type: (v[0] as AffiliatedOrganizationType | undefined) ?? undefined }),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<AffiliatedOrganizationListItem, AffiliatedOrganizationSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.affiliated_organization_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("table.editLabel", { name: r.name })}
									onEdit={() => setForm({ mode: "edit", organization: r })}
									deleteLabel={t("table.deleteLabel", { name: r.name })}
									onDelete={() => del.ask(r)}
									canDelete={r.cards_count === 0}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <AffiliatedOrganizationFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
